<template>
  <b-card
    class="list-view order-items-list"
    bg-variant="transparent"
    style="border: 1px dashed;border-radius: 10px;"
  >
    <b-card-title class="d-flex align-items-center justify-content-between">
      <label
        v-t="`modules.orders._create.order_content`"
        class="section-label"
      />

      <div
        v-if="canCreateShipment"
        class="d-flex align-items-center"
      >
        <div class="d-flex align-items-center ml-2">
          <b-button
            v-b-tooltip.hover="$t('modules.orders._shipments._create.description')"
            class="mr-1"
            variant="info"
            @click="$emit('click-create-waybill')"
          >
            <feather-icon icon="TruckIcon" />
            {{ $t('modules.orders._shipments._create.title') }}
          </b-button>
          <b-button
            v-b-tooltip.hover="$t('modules.orders._shipments._mark_as_fulfilled.description')"
            variant="outline-secondary"
            @click="$emit('click-mark-as-shipped')"
          >
            <feather-icon icon="CheckIcon" />
            {{ $t('modules.orders._shipments._mark_as_fulfilled.title') }}
          </b-button>
        </div>
      </div>
    </b-card-title>
    <div class="checkout-items">
      <!-- Product Selection Actions -->
      <div
        v-if="!readonly"
        class="product-actions-bar mb-2"
      >
        <b-button
          variant="primary"
          class="add-product-btn"
          @click="openProductModal"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
          />
          {{ $t('modules.orders._create.add_product') }}
        </b-button>
        <b-button
          variant="outline-primary"
          class="add-custom-btn"
          @click="openCustomItemModal"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
          />
          {{ $t('modules.orders._create.add_custom_item') }}
        </b-button>
      </div>

      <!-- Product Select Modal -->
      <product-select-modal
        :is-visible="isProductModalVisible"
        :products="options"
        :existing-products="products"
        @update:is-visible="v => isProductModalVisible = v"
        @products-selected="onProductsSelected"
      />

      <!-- Custom Item Modal -->
      <b-modal
        id="custom-item-modal"
        v-model="isCustomItemModalVisible"
        :title="$t('Add custom item')"
        centered
        hide-footer
      >
        <b-form @submit.prevent="addCustomItem">
          <b-form-group :label="$t('Item name')">
            <b-form-input
              v-model="customItem.name"
              :placeholder="$t('Enter item name')"
              required
            />
          </b-form-group>
          <b-form-group :label="$t('Price')">
            <cleave
              v-model="customItem.price"
              class="form-control"
              :raw="false"
              :options="{numeral: true, numeralThousandsGroupStyle: 'thousand', numeralDecimalScale: 2}"
              placeholder="0.00"
            />
          </b-form-group>
          <b-form-group :label="$t('Quantity')">
            <div class="qty-input-wrapper qty-input-full-width">
              <button
                type="button"
                class="qty-btn qty-btn-minus"
                :disabled="customItem.selected_qty <= 1"
                @click="customItem.selected_qty = Math.max(1, customItem.selected_qty - 1)"
              >
                <feather-icon icon="MinusIcon" size="14" />
              </button>
              <input
                v-model.number="customItem.selected_qty"
                type="number"
                class="qty-input"
                :min="1"
                @blur="customItem.selected_qty = Math.max(1, customItem.selected_qty || 1)"
              >
              <button
                type="button"
                class="qty-btn qty-btn-plus"
                @click="customItem.selected_qty += 1"
              >
                <feather-icon icon="PlusIcon" size="14" />
              </button>
            </div>
          </b-form-group>
          <div class="d-flex justify-content-end gap-2 mt-2">
            <b-button
              variant="outline-secondary"
              @click="isCustomItemModalVisible = false"
            >
              {{ $t('Cancel') }}
            </b-button>
            <b-button
              variant="primary"
              :disabled="!customItem.name || !customItem.price"
              @click="addCustomItem"
            >
              {{ $t('Add') }}
            </b-button>
          </div>
        </b-form>
      </b-modal>

      <!-- Order Items Header -->
      <div
        v-if="products.length"
        class="order-items-header"
      >
        <div
          v-if="(orderStatus === 'PRCS') && canUseFeature('order_items_preparation_progress')"
          class="header-checkbox"
        />
        <div class="header-image" />
        <div class="header-product">{{ $t('Product') }}</div>
        <div class="header-price">{{ $t('Price') }}</div>
        <div class="header-qty">{{ $t('Qty') }}</div>
        <div class="header-total">{{ $t('Total') }}</div>
        <div
          v-if="!readonly"
          class="header-actions"
        />
      </div>

      <!-- Existent Items - Compact List -->
      <div
        v-for="product in products"
        :key="product.id"
        class="order-item-row"
      >
        <!-- Prepared Checkbox -->
        <div
          v-if="(orderStatus === 'PRCS') && canUseFeature('order_items_preparation_progress')"
          class="order-item-checkbox"
        >
          <b-form-checkbox
            v-b-tooltip.hover="$t(!!product.prepared_at ? 'modules.orders._create.mark_as_not_prepared' : 'modules.orders._create.mark_as_prepared')"
            :checked="!!product.prepared_at"
            @change="val => toggleItemPrepared(product, val)"
          />
        </div>

        <!-- Product Image -->
        <div class="order-item-image">
          <b-img
            :src="product.image ? storagePath(product.image) : ImagePlaceholder"
            :alt="product.name"
          />
        </div>

        <!-- Product Info -->
        <div class="order-item-info">
          <div class="order-item-name">
            <b-link
              v-if="product.product_group_id"
              class="text-body font-weight-medium"
              :to="{name: 'products.edit', params: {id: product.product_group_id}}"
              target="_blank"
            >
              {{ product.name }}
            </b-link>
            <span
              v-else
              class="font-weight-medium"
            >{{ product.name }}</span>
          </div>
          <div
            v-if="product.options_values && product.options_values.length"
            class="order-item-variants"
          >
            <span
              v-for="(optionValue, k) in product.options_values"
              :key="`option-value-${k}`"
            >
              {{ optionValue[`value_${$i18n.locale}`] }}<span v-if="k < product.options_values.length - 1">, </span>
            </span>
          </div>
          <div
            v-if="product.properties && product.properties.length"
            class="order-item-properties"
          >
            <span
              v-for="(property, k) in product.properties"
              :key="property.id"
            >
              {{ property.label }}: {{ property.value }}<span v-if="k < product.properties.length - 1"> · </span>
            </span>
          </div>
        </div>

        <!-- Unit Price -->
        <div class="order-item-price">
          <div
              v-if="!readonly && editingPriceProductId === (product.id || product.product_id)"
              class="d-inline-flex align-items-center"
          >
            <cleave
                :id="`price-edit-${product.id || product.product_id}`"
                v-model="product.price"
                class="form-control form-control-sm price-input-compact"
                :raw="false"
                :options="{numeral: true, numeralThousandsGroupStyle: 'thousand', numeralDecimalScale: 2}"
                placeholder="0.00"
                @input="triggerItemsUpdatedEvent"
                @keyup.enter.native="editingPriceProductId = null"
            />
            <button
                type="button"
                class="btn-check-price"
                @click="editingPriceProductId = null"
            >
              <feather-icon icon="CheckIcon" size="14" />
            </button>
          </div>
          <div
              v-else
              class="price-display-compact"
              :class="{ 'editable': !readonly }"
              @click="!readonly && startEditingPrice(product)"
          >
            {{ $n(parseFloat(String(product.price).replace(',','')), 'currency', 'en-US') }}
            <feather-icon
                v-if="!readonly"
                icon="Edit2Icon"
                size="12"
                class="edit-icon"
            />
          </div>
        </div>

        <!-- Quantity -->
        <div class="order-item-qty">
          <div class="qty-input-wrapper qty-input-compact">
            <button
              type="button"
              class="qty-btn qty-btn-minus"
              :disabled="readonly || product.selected_qty <= 1"
              @click="decrementQty(product)"
            >
              <feather-icon icon="MinusIcon" size="12" />
            </button>
            <input
              v-model.number="product.selected_qty"
              type="number"
              class="qty-input"
              :min="1"
              :max="product.has_unlimited_qty ? null : product.qty"
              :disabled="readonly"
              @input="onQtyInput(product)"
              @blur="validateQty(product)"
            >
            <button
              type="button"
              class="qty-btn qty-btn-plus"
              :disabled="readonly || (!product.has_unlimited_qty && product.selected_qty >= product.qty)"
              @click="incrementQty(product)"
            >
              <feather-icon icon="PlusIcon" size="12" />
            </button>
          </div>
        </div>

        <!-- Total -->
        <div class="order-item-total">
          {{ $n((parseFloat(String(product.price).replace(',','')) || 0) * (product.selected_qty || 0), 'currency', 'en-US') }}
        </div>

        <!-- Actions -->
        <div
          v-if="!readonly"
          class="order-item-actions"
        >
          <button
            type="button"
            class="btn-remove-item"
            @click="removeItem(product)"
          >
            <feather-icon icon="TrashIcon" size="16" />
          </button>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-if="!products.length"
        class="empty-state"
      >
        <div class="empty-state-icon">
          <feather-icon
            icon="ShoppingBagIcon"
            size="48"
          />
        </div>
        <h4 class="empty-state-title">
          {{ $t('modules.orders._create._messages.no_items') }}
        </h4>
        <p
          v-if="!readonly"
          class="empty-state-description"
        >
          {{ $t('modules.orders._create._messages.add_items_from_box_above') }}
        </p>
        <div
          v-if="!readonly"
          class="empty-state-actions"
        >
          <b-button
            variant="primary"
            size="sm"
            @click="openProductModal"
          >
            <feather-icon
              icon="PlusIcon"
              class="mr-50"
            />
            {{ $t('modules.orders._create.add_product') }}
          </b-button>
        </div>
      </div>
    </div>

  </b-card>
</template>

<script>
import {
  BCard,
  BCardBody,
  BLink,
  BImg,
  BButton,
  BBadge,
  BFormInput,
  BFormGroup,
  BForm,
  BFormCheckbox,
  BModal,
  VBToggle,
  BAlert,
  VBTooltip,
} from 'bootstrap-vue'
import { ref, watch } from 'vue'
import axios from '@/libs/axios'
import jwtDefaultConfig from '@core/auth/jwt/jwtDefaultConfig'
import { formatDate } from '@core/utils/filter'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import Cleave from 'vue-cleave-component'
import { BCardTitle } from 'bootstrap-vue/src/components/card'
import { storagePath } from '@core/utils/utils'
import ProductSelectModal from './ProductSelectModal.vue'
import { canUseFeature, catchException, toast } from '@core/utils/utils'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
    'b-tooltip': VBTooltip,
  },
  components: {
    BCard,
    BCardBody,
    BLink,
    BImg,
    BButton,
    BBadge,
    BFormInput,
    BFormGroup,
    BForm,
    BFormCheckbox,
    BModal,
    vSelect,

    BCardTitle,
    Cleave,
    BAlert,
    ProductSelectModal,
  },
  props: {
    options: {
      type: Array,
      default: Array,
    },
    selected: {
      type: Object,
      default: Object,
    },
    products: {
      type: Array,
      default: Array,
    },
    readonly: {
      type: Boolean,
      default: true,
    },
    hasShipments: {
      type: Boolean,
      default: false,
    },
    canCreateShipment: {
      type: Boolean,
      default: false,
      required: false,
    },
    orderStatus: {
      type: String,
      default: null,
    },
  },
  watch: {
    products(newValue) {
      this.currentProducts = newValue
    },
  },
  methods: { storagePath, canUseFeature },
  emits: ['update:selected', 'update:products', 'click-create-waybill', 'click-mark-as-shipped'],
  setup(props, ctx) {
    // products.value = [
    //   // {
    //   //   id: 1,
    //   //   name: 'VicTsing Wireless Mouse,',
    //   //   category: 'VicTsing',
    //   //   price: 10.99,
    //   //   image: require('@/assets/images/pages/eCommerce/27.png'),
    //   //   free_shipping: true,
    //   //   selected_qty: 3,
    //   //   qty: 10,
    //   //   has_unlimited_qty: false,
    //   //   limit_per_order: 5,
    //   //   min_per_order: 2,
    //   //   shippingDate: new Date(new Date().getTime() + 24 * 60 * 60 * 1000),
    //   //   offers: 2,
    //   //   discountPercentage: 5,
    //   // },
    // ]

    const emptyItemData = {
      id: null,
      product_id: null,
      name: null,
      category: null,
      price: null,
      qty: null,
      has_unlimited_qty: true,
      limit_per_order: null,
      min_per_order: null,
      image: null,
      free_shipping: false,
      selected_qty: null,
      shippingDate: new Date(),
      offers: 0,
      discountPercentage: 0,
      items: [],
    }
    const item = ref({})
    const newItemCard = ref()
    const editingPriceProductId = ref(null)
    const isProductModalVisible = ref(false)
    const isCustomItemModalVisible = ref(false)

    const emptyCustomItem = {
      id: null,
      product_id: null,
      name: '',
      price: null,
      selected_qty: 1,
      is_custom: true,
    }
    const customItem = ref({ ...emptyCustomItem })

    const startEditingPrice = product => {
      editingPriceProductId.value = product.id || product.product_id
    }

    // Quantity input handlers
    const incrementQty = product => {
      const max = product.has_unlimited_qty ? Infinity : product.qty
      if (product.selected_qty < max) {
        product.selected_qty += 1
        triggerItemsUpdatedEvent()
      }
    }

    const decrementQty = product => {
      if (product.selected_qty > 1) {
        product.selected_qty -= 1
        triggerItemsUpdatedEvent()
      }
    }

    const onQtyInput = product => {
      triggerItemsUpdatedEvent()
    }

    const validateQty = product => {
      // Ensure quantity is at least 1
      if (!product.selected_qty || product.selected_qty < 1) {
        product.selected_qty = 1
      }
      // Ensure quantity doesn't exceed max
      if (!product.has_unlimited_qty && product.selected_qty > product.qty) {
        product.selected_qty = product.qty
      }
      // Ensure it's an integer
      product.selected_qty = Math.floor(product.selected_qty)
      triggerItemsUpdatedEvent()
    }

    const resetItemData = () => {
      item.value = JSON.parse(JSON.stringify(emptyItemData))
      item.value.selected_qty = null
    }

    const openProductModal = () => {
      isProductModalVisible.value = true
    }

    const openCustomItemModal = () => {
      customItem.value = { ...emptyCustomItem }
      isCustomItemModalVisible.value = true
    }

    resetItemData()

    const ImagePlaceholder = require('@/assets/images/pages/eCommerce/placeholder.webp')

    const products = ref([...props.products])
    const triggerItemsUpdatedEvent = () => {
      ctx.emit('update:products', products.value)
    }
    watch(products, () => {
      triggerItemsUpdatedEvent()
    }, { deep: true })

    const updateItemInfo = value => {
      if (!value) {
        resetItemData()
        return
      }
      // item.value = JSON.parse(JSON.stringify(val))
      const val = JSON.parse(JSON.stringify(value))
      item.value.product_id = val.id || val.product_id
      item.value.name = val.name
      item.value.category = val.category
      item.value.price = val.price.amount / 100
      item.value.qty = val.qty
      item.value.has_unlimited_qty = val.has_unlimited_qty
      item.value.limit_per_order = val.limit_per_order
      item.value.min_per_order = val.min_per_order
      item.value.image = val.image
      item.value.free_shipping = val.free_shipping

      const productIndexInCart = getProductIndexInCart(item.value.product_id)
      item.value.selected_qty = val.selected_qty || val.min_per_order || 1
      if (productIndexInCart >= 0) {
        item.value.selected_qty = products.value[productIndexInCart].selected_qty
        item.value.price = products.value[productIndexInCart].price
      }
      if (!val.has_unlimited_qty && item.value.selected_qty > val.qty) {
        item.value.selected_qty = 0
      }
    }

    const onClickItemEdit = product => {
      if (newItemCard.value) {
        newItemCard.value.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        })
      }
      updateItemInfo({ ...product })
    }

    const getProductIndexInCart = itemId => {
      // Search in attribute 'id' of items
      const resultOfSearchById = products.value.findIndex(p => p.id === itemId)
      if (resultOfSearchById > -1) {
        return resultOfSearchById
      }
      // Search in attribute 'product_id' of items
      return products.value.findIndex(p => p.product_id === itemId)
    }
    const isProductExistsInCart = productId => getProductIndexInCart(productId) >= 0
    const addItem = () => {
      if (item.value.product_id) {
        const productIndex = getProductIndexInCart(item.value.product_id)
        if (productIndex >= 0) {
          let newQty = item.value.selected_qty /* + products.value[productIndex].selected_qty */
          if (!item.value.has_unlimited_qty && newQty > item.value.qty) {
            newQty = Math.min(newQty, item.value.qty)
          }
          products.value[productIndex].selected_qty = newQty
          products.value[productIndex].price = item.value.price
        } else {
          products.value.unshift({
            ...JSON.parse(JSON.stringify(item.value)),
          })
        }
        resetItemData()
      }
    }

    const removeItem = orderItem => {
      const itemId = orderItem.id
      const productIndex = getProductIndexInCart(itemId)
      if (productIndex >= 0) {
        products.value.splice(productIndex, 1)
      }
    }

    const onProductsSelected = selectedProducts => {
      selectedProducts.forEach(product => {
        const productId = product.id || product.product_id
        const existingIndex = getProductIndexInCart(productId)

        if (existingIndex >= 0) {
          // Update existing product quantity
          products.value[existingIndex].selected_qty += 1
        } else {
          // Add new product
          const price = typeof product.price === 'object'
            ? product.price.amount / 100
            : product.price
          products.value.unshift({
            id: productId,
            product_id: productId,
            product_group_id: product.group_id || product.product_group_id,
            name: product.name,
            category: product.category,
            price,
            qty: product.qty,
            has_unlimited_qty: product.has_unlimited_qty,
            limit_per_order: product.limit_per_order,
            min_per_order: product.min_per_order,
            image: product.group_image || product.image,
            free_shipping: product.has_free_shipping,
            selected_qty: product.min_per_order || 1,
            options_values: product.options_values,
            currency_code: product.currency_code,
          })
        }
      })
    }

    const toggleItemPrepared = (product, checked) => {
      const previousValue = product.prepared_at
      product.prepared_at = checked ? new Date().toISOString() : null

      axios
        .put(`${jwtDefaultConfig.BACKEND_HOST}/${jwtDefaultConfig.DASHBOARD_ENDPOINT}/order-items/${product.id}`, { prepared_at: checked })
        .catch(() => {
          product.prepared_at = previousValue
        })
    }

    const addCustomItem = () => {
      if (!customItem.value.name || !customItem.value.price) return

      const virtualProductId = `custom-${Date.now()}`
      products.value.unshift({
        id: virtualProductId,
        product_id: 0,
        name: customItem.value.name,
        price: parseFloat(String(customItem.value.price).replace(',', '')),
        selected_qty: customItem.value.selected_qty || 1,
        is_custom: true,
        has_unlimited_qty: true,
      })

      isCustomItemModalVisible.value = false
      customItem.value = { ...emptyCustomItem }
    }

    return {
      // products,
      currentProducts: products,
      item,
      onClickItemEdit,
      updateItemInfo,
      addItem,
      removeItem,
      resetItemData,
      triggerItemsUpdatedEvent,
      isProductExistsInCart,
      editingPriceProductId,
      startEditingPrice,
      incrementQty,
      decrementQty,
      onQtyInput,
      validateQty,

      // Product Modal
      isProductModalVisible,
      isCustomItemModalVisible,
      customItem,
      openProductModal,
      openCustomItemModal,
      onProductsSelected,
      addCustomItem,
      toggleItemPrepared,

      // Refs
      newItemCard,

      // UI
      ImagePlaceholder,

      // Filter
      formatDate,
    }
  },
}
</script>

<style>
[dir] .order-items-list .v-select ul {
  position: absolute;
  top: auto;
  left: auto;
  width: inherit;
}

[dir] .ecommerce-application .ecommerce-card.empty-card:hover {
  transform: unset;
  box-shadow: unset;
}
.ecommerce-application .list-view .ecommerce-card {
  overflow: unset !important;
}

[dir] .ecommerce-application .checkout-items .ecommerce-card .item-img img {
  height: 120px;
  width: auto;
}

.checkout-items .card-body .qty-price-row {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}
.checkout-items .card-body .qty-price-row .row-item {
  margin: 0.5rem 1rem;
  flex: 1 1 12rem;
  min-width: 12rem;
  max-width: -webkit-fit-content;
  max-width: -moz-fit-content;
  max-width: fit-content;
}

/* Inline Price Editor Styles */
.price-edit-inline {
  animation: fadeIn 0.2s ease-in-out;
}

.price-input-inline {
  max-width: 100px;
  text-align: center;
}

.price-display-inline {
  padding: 0.25rem 0.5rem;
  border-radius: 0.357rem;
  transition: background-color 0.15s ease-in-out;
}

.price-display-inline:hover {
  background-color: rgba(115, 103, 240, 0.08);
}

.price-display-inline .price-edit-icon {
  opacity: 0;
  transition: opacity 0.15s ease-in-out;
}

.price-display-inline:hover .price-edit-icon {
  opacity: 1;
}

.cursor-pointer {
  cursor: pointer;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Quantity Input Styles */
.qty-input-wrapper {
  display: inline-flex;
  align-items: center;
  border: 2px solid #e0e0e0;
  border-radius: 25px;
  overflow: hidden;
  background: #fff;
  height: 38px;
}

.qty-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 100%;
  border: none;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.qty-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.05);
  color: #495057;
}

.qty-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.qty-btn:focus {
  outline: none;
}

.qty-input {
  width: 50px;
  height: 100%;
  border: none;
  background: transparent;
  text-align: center;
  font-weight: 500;
  font-size: 0.95rem;
  color: #5e5873;
  -moz-appearance: textfield;
}

.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.qty-input:focus {
  outline: none;
}

.qty-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.qty-input-full-width {
  width: 100%;
  justify-content: space-between;
}

.qty-input-full-width .qty-input {
  flex: 1;
  width: auto;
}

/* Product Actions Bar Styles */
.product-actions-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.product-actions-bar .add-product-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(40, 199, 111, 0.3);
}

.product-actions-bar .add-custom-btn:hover {
  transform: translateY(-1px);
}

/* Gap utility */
.gap-2 {
  gap: 0.5rem;
}

/* Empty State Styles */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
  border-radius: 12px;
  border: 2px dashed #e0e0e0;
  margin-top: 1rem;
}

.empty-state-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #28c76f15 0%, #28c76f08 100%);
  margin-bottom: 1.5rem;
  color: #28c76f;
}

.empty-state-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #5e5873;
  margin-bottom: 0.5rem;
}

.empty-state-description {
  font-size: 0.9rem;
  color: #a0a0a0;
  margin-bottom: 1.5rem;
  max-width: 300px;
}

.empty-state-actions {
  display: flex;
  gap: 0.75rem;
}

.empty-state-actions .btn {
  border-radius: 8px;
  padding: 0.6rem 1.25rem;
  font-weight: 500;
}

/* Order Items Header */
.order-items-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 2px solid #e0e0e0;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.order-items-header .header-checkbox {
  width: 28px;
  flex-shrink: 0;
}

.order-items-header .header-image {
  width: 50px;
  flex-shrink: 0;
}

.order-items-header .header-product {
  flex: 1;
  min-width: 0;
}

.order-items-header .header-price {
  min-width: 90px;
  text-align: center;
}

.order-items-header .header-qty {
  min-width: 100px;
  text-align: center;
}

.order-items-header .header-total {
  min-width: 100px;
  text-align: right;
}

.order-items-header .header-actions {
  width: 32px;
  flex-shrink: 0;
}

/* Compact Order Item Row Styles */
.order-item-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e0e0e0;
}

.order-item-row:last-child {
  border-bottom: none;
}

.order-item-checkbox {
  flex-shrink: 0;
  width: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.order-item-image {
  flex-shrink: 0;
  width: 50px;
  height: 50px;
}

.order-item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.order-item-info {
  flex: 1;
  min-width: 0;
}

.order-item-name {
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 1.4;
  margin-bottom: 3px;
  color: #5e5873;
}

.order-item-name a {
  color: #5e5873;
}

.order-item-name a:hover {
  color: #28c76f !important;
}

.order-item-variants {
  font-size: 1rem;
  line-height: 1.4;
  color: #6e6b7b;
  margin-bottom: 2px;
}

.order-item-properties {
  font-size: 1rem;
  line-height: 1.4;
  color: #82868b;
}

.order-item-qty {
  flex-shrink: 0;
}

.qty-input-compact {
  height: 32px;
}

.qty-input-compact .qty-btn {
  width: 28px;
}

.qty-input-compact .qty-input {
  width: 40px;
  font-size: 0.85rem;
}

.order-item-price {
  flex-shrink: 0;
  min-width: 90px;
  text-align: right;
}

.price-display-compact {
  font-size: 0.9rem;
  color: #6c757d;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.15s ease;
}

.price-display-compact.editable {
  cursor: pointer;
}

.price-display-compact.editable:hover {
  background-color: #f0f0f0;
}

.price-display-compact .edit-icon {
  opacity: 0;
  transition: opacity 0.15s ease;
  color: #28c76f;
}

.price-display-compact.editable:hover .edit-icon {
  opacity: 1;
}

.price-input-compact {
  width: 80px;
  font-size: 0.85rem;
  padding: 0.25rem 0.5rem;
  text-align: center;
}

.btn-check-price {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: #28c76f;
  color: white;
  border-radius: 4px;
  margin-left: 4px;
  cursor: pointer;
}

.btn-check-price:hover {
  background: #24b263;
}

.order-item-total {
  flex-shrink: 0;
  min-width: 100px;
  text-align: right;
  font-weight: 600;
  font-size: 0.95rem;
  color: #28c76f;
}

.order-item-actions {
  flex-shrink: 0;
}

.btn-remove-item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #ea5455;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.btn-remove-item:hover {
  background: #fceaea;
}

/* Responsive adjustments for compact items */
@media (max-width: 768px) {
  .order-item-row {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .order-item-info {
    flex: 1 1 calc(100% - 70px);
  }

  .order-item-qty,
  .order-item-price,
  .order-item-total {
    flex: 1;
    text-align: center;
  }

  .order-item-actions {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
  }
}
</style>
